import React, { useState, useEffect, useRef } from 'react';
import { portalAPI, userApplicationAPI, categoryAPI, favoritesAPI, profileAPI } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import MFASetupPopup from './MFASetupPopup';
import CreatePortalCard from './CreatePortalCard';
import { UserCircleIcon, Cog6ToothIcon, StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { PageLayout, ConfirmDialog } from './ui';
import { useAuth } from '../context/AuthContext';

const PortalCard = ({ portal, onDelete, layoutDensity = 'standard' }) => {
  const navigate = useNavigate();
  const isPersonalPortal = portal.is_personal === true;
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.permission_level === 'global_admin';
  const userId = authUser?.id?.toString();
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (forAllUsers) => {
    setConfirmDialog({
      open: true,
      title: forAllUsers ? 'Delete Portal for All Users' : 'Remove Portal',
      message: `Are you sure you want to ${forAllUsers ? 'delete' : 'remove'} this portal${forAllUsers ? ' for all users' : ''}?`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          if (forAllUsers) {
            if (!isAdmin) {
              throw new Error('Only admins can delete portals for all users');
            }
            await portalAPI.deletePortal(portal.id);
          } else {
            await portalAPI.disassociatePortal(portal.id);
          }
          onDelete(portal.id);
        } catch (error) {
          console.error('Failed to delete portal:', error);
          alert(error.message || 'Failed to delete portal');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
    setShowDropdown(false);
  };

  const canDelete = isAdmin || portal.created_by === userId;
  const handlePortalClick = async () => {
    if (!portal?.url) return;

    if (portal.via_proxy) {
      // Create the proxy URL with the target URL encoded
      const targetUrl = encodeURIComponent(portal.url);
      const proxyUrl = `${window.location.origin}/api/portals/proxy/${portal.id}?url=${targetUrl}`;
      
      // Create the modal first
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      modal.innerHTML = `
        <div class="bg-white w-11/12 h-5/6 rounded-lg shadow-lg flex flex-col">
          <div class="flex justify-between items-center p-4 border-b">
            <h3 class="text-lg font-semibold">${portal.name}</h3>
            <button class="text-gray-500 hover:text-gray-700" id="close-proxy-modal">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="flex-1 p-4" id="iframe-container-${portal.id}">
            <iframe 
              id="proxy-frame-${portal.id}"
              name="proxy-frame-${portal.id}"
              class="w-full h-full border-0"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-modals"
            ></iframe>
          </div>
        </div>
      `;

      // Add the modal to the document
      document.body.appendChild(modal);

      // Make request — auth is via HttpOnly cookies (credentials: 'include')
      fetch(proxyUrl + '?external_domain=paperlite.timg.com', {
        method: 'GET',
        headers: { 'Accept': '*/*' },
        credentials: 'include'
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then(content => {
        // Get the iframe and write content
        const frame = document.getElementById(`proxy-frame-${portal.id}`);
        if (frame && frame.contentWindow) {
          try {
            frame.contentWindow.document.open();
            frame.contentWindow.document.write(content);
            frame.contentWindow.document.close();
          } catch (writeError) {
            console.error('Error writing to iframe:', writeError);
            throw writeError;
          }
        } else {
          throw new Error('Could not access iframe or its content window');
        }
      })
      .catch(error => {
        console.error('Error loading portal:', error.message);
        alert(`Failed to load portal content: ${error.message}`);
      });

      // Set up the close button handler
      document.getElementById('close-proxy-modal').onclick = () => {
        if (modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      };
    } else {
      // For external URLs, open in a new tab as before
      window.open(portal.url, '_blank');
    }
  };

  const isCompact = layoutDensity === 'compact';
  const isComfortable = layoutDensity === 'comfortable';

  if (isCompact) {
    return (
      <div
        onClick={handlePortalClick}
        className="bg-white rounded-xl border border-gray-100 p-3.5 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between h-20 relative group shadow-sm"
      >
        <div className="flex items-center gap-3 min-w-0">
          {portal.icon_url ? (
            <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center p-1.5 border border-gray-100 group-hover:border-indigo-100 transition-colors shadow-inner flex-shrink-0 animate-fade">
              <img
                src={portal.icon_url}
                alt={portal.name}
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <span class="text-sm font-bold text-indigo-600">${portal.name.charAt(0)}</span>
                  `;
                }}
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-indigo-50/70 border border-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-base shadow-inner group-hover:bg-indigo-100/50 transition-colors flex-shrink-0">
              {portal.name.charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-gray-800 text-sm tracking-tight truncate group-hover:text-indigo-600 transition-colors">
              {portal.name}
            </h3>
            <span className="text-[9px] font-semibold text-indigo-500 uppercase tracking-wider bg-indigo-50 px-1.5 py-0.5 rounded">
              Portal
            </span>
          </div>
        </div>

        {/* Portal Menu */}
        <div className="portal-menu relative flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </button>

          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute right-0 top-6 w-48 rounded-xl shadow-lg bg-white border border-gray-100 py-1 z-20 focus:outline-none animate-fade"
            >
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/portals/${portal.id}/settings`);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Settings
                </button>
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(true);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Delete for all users
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Remove for me
                </button>
              </div>
            </div>
          )}
        </div>

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.title.startsWith('Delete') ? 'Delete' : 'Confirm'}
          variant={confirmDialog.variant}
        />
      </div>
    );
  }

  const heightClass = isComfortable ? 'h-48 p-6' : 'h-44 p-5';
  const iconSizeClass = isComfortable ? 'w-14 h-14 rounded-2xl text-2xl' : 'w-12 h-12 rounded-xl text-xl';

  return (
    <div
      onClick={handlePortalClick}
      className={`bg-white rounded-2xl border border-gray-100 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between ${heightClass} relative group shadow-[0_8px_30px_rgb(0,0,0,0.04)]`}
    >
      <div>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            {portal.icon_url ? (
              <div className={`${iconSizeClass} bg-gray-50 flex items-center justify-center p-2 border border-gray-100 group-hover:border-indigo-100 transition-colors shadow-inner`}>
                <img
                  src={portal.icon_url}
                  alt={portal.name}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <span class="font-bold text-indigo-600">
                        ${portal.name.charAt(0)}
                      </span>
                    `;
                  }}
                />
              </div>
            ) : (
              <div className={`${iconSizeClass} bg-indigo-50/70 border border-indigo-100 flex items-center justify-center text-indigo-600 font-bold shadow-inner group-hover:bg-indigo-100/50 transition-colors`}>
                {portal.name.charAt(0)}
              </div>
            )}
            {isPersonalPortal && (
              <span className="ml-2.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                Personal
              </span>
            )}
          </div>

          {/* Portal Menu */}
          <div className="portal-menu relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
              }}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none"
            >
              <Cog6ToothIcon className="h-4.5 w-4.5" />
            </button>

            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute right-0 mt-1.5 w-48 rounded-xl shadow-lg bg-white border border-gray-100 py-1 z-20 focus:outline-none"
              >
                <div className="py-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/portals/${portal.id}/settings`);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Settings
                  </button>
                  {canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(true);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Delete for all users
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Remove for me
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <h3 className="font-bold text-gray-800 text-base mb-1 tracking-tight truncate group-hover:text-indigo-600 transition-colors">
          {portal.name}
        </h3>
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
          {portal.description || 'Access linked portal'}
        </p>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wider bg-indigo-50 px-2 py-0.5 rounded-md">
          Portal
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:text-indigo-700 transition-colors">
          Open
          <svg className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </span>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.title.startsWith('Delete') ? 'Delete' : 'Confirm'}
        variant={confirmDialog.variant}
      />
    </div>
  );
};

const ICON_PRESETS = [
  '📁', '📂', '🗂️', '📑', // Folders
  '🔑', '🔐', '🛡️', '🔒', // Access / Security
  '🏢', '👥', '🤝', '💼', // Organization
  '🖥️', '☁️', '⚡', '⚙️',  // Tech
  '📊', '💰', '📈', '🎨', // Finance / Analytics / Art
  '🚀', '🌐', '📣', '🔔'  // Actions / General
];

const HomePage = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [portals, setPortals] = useState([]);
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uncategorizedApps, setUncategorizedApps] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMFAPopup, setShowMFAPopup] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', icon: '📁' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [draggedApp, setDraggedApp] = useState(null);
  const [draggedOverAppId, setDraggedOverAppId] = useState(null);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const [layoutDensity, setLayoutDensity] = useState(() => localStorage.getItem('appLayoutDensity') || 'standard');
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(() => localStorage.getItem('dismissedWelcomeBanner') !== 'true');
  const [sessionNotice, setSessionNotice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
  const [redirecting, setRedirecting] = useState(false);
  const [redirectApp, setRedirectApp] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  const getGridColsClass = () => {
    if (layoutDensity === 'comfortable') {
      return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6';
    }
    if (layoutDensity === 'compact') {
      return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4';
    }
    // Standard — tighter grid, more columns on wider screens
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4';
  };

  useEffect(() => {
    // Check for session displacement notification
    const loginMessage = localStorage.getItem('login_message');
    const loginMessageType = localStorage.getItem('login_message_type');
    if (loginMessage && loginMessageType === 'info') {
      setSessionNotice(loginMessage);
      localStorage.removeItem('login_message');
      localStorage.removeItem('login_message_type');
      setTimeout(() => setSessionNotice(null), 10000);
    }

    // Check for SAML login redirect
    const params = new URLSearchParams(window.location.search);
    const samlLogin = params.get('saml_login');
    const appId = params.get('app_id');
    
    if (samlLogin === 'true' && appId) {
      // User is authenticated (inside ProtectedRoute) — resume the SAML flow.
      // Auth is via HttpOnly cookies; the continue endpoint uses Flask session
      // and falls back to JWT for user identity.
      window.location.href = `/api/saml/sso/${appId}/continue`;
    }
    
    const fetchData = async () => {
      try {
        // Fetch portals, applications, categories, favorites, and user profile
        const [portalsData, appsData, categoriesData, favoritesData, profileData] = await Promise.all([
          portalAPI.getPortals().catch(() => ({ portals: [] })),
          userApplicationAPI.getMyApplications().catch(() => ({ applications: [] })),
          categoryAPI.getCategories().catch(() => ({ categories: [], uncategorized: [] })),
          favoritesAPI.getFavorites().catch(() => ({ favorites: [] })),
          profileAPI.getProfile().catch(() => null)
        ]);
        
        if (profileData && profileData.status === 'success') {
          setUserProfile(profileData.user);

          // Show MFA popup if user hasn't enabled MFA, isn't an SSO user,
          // and hasn't dismissed the popup this session
          if (!profileData.user.mfa_enabled
              && profileData.user.auth_method !== 'sso'
              && localStorage.getItem('mfa_popup_dismissed') !== 'true') {
            setShowMFAPopup(true);
          }
        }
        
        if (portalsData.portals) {
          setPortals(portalsData.portals);
        } else if (Array.isArray(portalsData)) {
          setPortals(portalsData);
        }
        
        if (appsData.applications) {
          setApplications(appsData.applications);
          
          // Auto-redirect: if user has exactly 1 app, not admin, not impersonating.
          // Fail closed: if profile fetch failed, we don't know the role — do NOT redirect.
          // Skip if navigated via back button or already redirected this session.
          if (profileData && profileData.status === 'success') {
            const userIsAdmin = profileData.user.permission_level === 'global_admin';
            const userIsImpersonating = !!(profileData.user.impersonating);
            const isBackNavigation = (performance.getEntriesByType?.('navigation')[0]?.type) === 'back_forward';
            if (!userIsAdmin && !userIsImpersonating && appsData.applications.length === 1 && !isBackNavigation && !sessionStorage.getItem('auto_redirected')) {
              setRedirecting(true);
              setRedirectApp(appsData.applications[0]);
              return;
            }
          }
        }
        
        // Set categories and uncategorized apps
        if (categoriesData.categories) {
          setCategories(categoriesData.categories);
        }
        if (categoriesData.uncategorized) {
          setUncategorizedApps(categoriesData.uncategorized);
        }
        
        // Set favorites and build a Set of favorite IDs for quick lookup
        if (favoritesData.favorites) {
          setFavorites(favoritesData.favorites);
          setFavoriteIds(new Set(favoritesData.favorites.map(f => f.id)));
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-redirect countdown
  useEffect(() => {
    if (!redirecting || !redirectApp) return;
    if (redirectCountdown <= 0) {
      sessionStorage.setItem('auto_redirected', 'true');
      window.location.replace(redirectApp.sign_on_url);
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirecting, redirectCountdown, redirectApp]);

  // Clear redirect flag on explicit navigation back to dashboard
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('from') === 'app') {
      sessionStorage.removeItem('auto_redirected');
    }
  }, []);

  const navigate = useNavigate();

  const handleApplicationClick = (app) => {
    // Direct access to application
    if (app.sign_on_url) {
      window.open(app.sign_on_url, '_blank');
    }
  };

  const handleToggleFavorite = async (e, app) => {
    e.stopPropagation();
    try {
      const result = await favoritesAPI.toggleFavorite(app.id);
      if (result.is_favorite) {
        setFavorites([...favorites, app]);
        setFavoriteIds(new Set([...favoriteIds, app.id]));
      } else {
        setFavorites(favorites.filter(f => f.id !== app.id));
        const newIds = new Set(favoriteIds);
        newIds.delete(app.id);
        setFavoriteIds(newIds);
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const toggleCategoryCollapse = (categoryId) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(categoryId)) {
      newCollapsed.delete(categoryId);
    } else {
      newCollapsed.add(categoryId);
    }
    setCollapsedCategories(newCollapsed);
  };

  // Category management handlers (admin only)
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoryAPI.createCategory(newCategory);
      setShowNewCategoryForm(false);
      setNewCategory({ name: '', description: '', icon: '📁' });
      // Reload categories
      const data = await categoryAPI.getCategories();
      setCategories(data.categories || []);
      setUncategorizedApps(data.uncategorized || []);
    } catch (err) {
      setError('Failed to create category');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoryAPI.updateCategory(editingCategory.id, editingCategory);
      setEditingCategory(null);
      const data = await categoryAPI.getCategories();
      setCategories(data.categories || []);
      setUncategorizedApps(data.uncategorized || []);
    } catch (err) {
      setError('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Category',
      message: 'Delete this category? Applications will become uncategorized.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await categoryAPI.deleteCategory(categoryId);
          const data = await categoryAPI.getCategories();
          setCategories(data.categories || []);
          setUncategorizedApps(data.uncategorized || []);
        } catch (err) {
          setError('Failed to delete category');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  const handleDragStart = (e, app) => {
    e.stopPropagation();
    setDraggedApp(app);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(app.id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, categoryId) => {
    e.preventDefault();
    if (!draggedApp) return;
    try {
      await categoryAPI.assignAppToCategory(categoryId, draggedApp.id);
      const data = await categoryAPI.getCategories();
      setCategories(data.categories || []);
      setUncategorizedApps(data.uncategorized || []);
    } catch (err) {
      setError('Failed to move application');
    }
    setDraggedApp(null);
    setDraggedOverAppId(null);
  };

  const handleDropOnApp = async (e, targetCategoryId, targetApp) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedApp) return;
    if (draggedApp.id === targetApp.id) return;

    try {
      const sourceCategoryId = draggedApp.category_id || 0;

      // If moving to a different category first
      if (sourceCategoryId !== targetCategoryId) {
        await categoryAPI.assignAppToCategory(targetCategoryId, draggedApp.id);
      }

      // Fetch updated category details to locate latest elements list
      const data = await categoryAPI.getCategories();
      const updatedCategories = data.categories || [];
      const updatedUncategorized = data.uncategorized || [];

      // Extract the applications of the target category
      let appList = [];
      if (targetCategoryId === 0) {
        appList = [...updatedUncategorized];
      } else {
        const targetCategory = updatedCategories.find(c => c.id === targetCategoryId);
        appList = targetCategory ? [...(targetCategory.applications || [])] : [];
      }

      // Remove draggedApp if it's already there to calculate clean insertion
      appList = appList.filter(a => a.id !== draggedApp.id);

      // Find insertion index (inserting directly before the targetApp)
      const targetIndex = appList.findIndex(a => a.id === targetApp.id);
      
      // Splicing the draggedApp in
      appList.splice(targetIndex, 0, { ...draggedApp, category_id: targetCategoryId === 0 ? null : targetCategoryId });

      // Obtain array of app IDs
      const order = appList.map(a => a.id);

      // Send the order to the backend reorder-apps endpoint
      await categoryAPI.reorderAppsInCategory(targetCategoryId, order);

      // Reload category state
      const refreshedData = await categoryAPI.getCategories();
      setCategories(refreshedData.categories || []);
      setUncategorizedApps(refreshedData.uncategorized || []);
    } catch (err) {
      console.error('Failed to reorder/move application:', err);
      setError('Failed to reorder application');
    } finally {
      setDraggedApp(null);
      setDraggedOverAppId(null);
    }
  };

  const handleCategoryDragStart = (e, categoryId) => {
    e.stopPropagation();
    setDraggedCategoryId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDrop = async (e, targetCategoryId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedCategoryId === null) return;
    if (draggedCategoryId === targetCategoryId) return;

    try {
      let catList = [...categories];
      const draggedIndex = catList.findIndex(c => c.id === draggedCategoryId);
      const targetIndex = catList.findIndex(c => c.id === targetCategoryId);

      if (draggedIndex !== -1 && targetIndex !== -1) {
        const [removed] = catList.splice(draggedIndex, 1);
        catList.splice(targetIndex, 0, removed);

        // Build array of category IDs in the new order
        const order = catList.map(c => c.id);

        // Send to backend
        await categoryAPI.reorderCategories(order);

        // Update local state instantly
        setCategories(catList);
      }
    } catch (err) {
      console.error('Failed to reorder categories:', err);
      setError('Failed to reorder categories');
    } finally {
      setDraggedCategoryId(null);
    }
  };

  // Render an application card
  const LCARS_COLORS = 8;
  const AppCard = ({ app, showFavorite = true, colorIndex = 0 }) => {
    const isCompact = layoutDensity === 'compact';
    const isComfortable = layoutDensity === 'comfortable';

    if (isCompact) {
      return (
        <div
          key={app.id}
          className={`bg-white border border-gray-100 rounded-xl p-3.5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-between h-20 relative group shadow-sm ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
          data-lcars-color={(colorIndex % LCARS_COLORS) + 1}
          onClick={editMode ? undefined : () => handleApplicationClick(app)}
        >
          <div className="flex items-center gap-3 min-w-0">
            {app.logo_url ? (
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center p-1.5 border border-gray-100 group-hover:border-indigo-100 transition-colors shadow-inner flex-shrink-0 animate-fade">
                <img 
                  src={app.logo_url}
                  alt="" 
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => { 
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    if (e.target.nextSibling?.className?.includes('fallback-icon')) return;
                    const fallback = document.createElement('div');
                    fallback.className = 'w-10 h-10 bg-indigo-50/70 border border-indigo-100 rounded-lg flex items-center justify-center text-base shadow-inner fallback-icon';
                    fallback.textContent = app.app_type === 'saml' ? '🔐' : app.app_type === 'bookmark' ? '🔖' : '🌐';
                    e.target.parentElement.insertBefore(fallback, e.target.nextSibling);
                  }}
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-indigo-50/70 border border-indigo-100 flex items-center justify-center text-lg shadow-inner group-hover:bg-indigo-100/50 transition-colors flex-shrink-0">
                {app.app_type === 'saml' ? '🔐' : app.app_type === 'bookmark' ? '🔖' : '🌐'}
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-bold text-gray-800 text-sm tracking-tight truncate group-hover:text-indigo-600 transition-colors">{app.label}</h3>
            </div>
          </div>

          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {showFavorite && (
              <button
                onClick={(e) => handleToggleFavorite(e, app)}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-300 hover:text-yellow-400 transition-all focus:outline-none"
                title={favoriteIds.has(app.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favoriteIds.has(app.id) ? (
                  <StarIcon className="h-4.5 w-4.5 text-yellow-400 scale-110" />
                ) : (
                  <StarOutlineIcon className="h-4.5 w-4.5 text-gray-300 hover:text-yellow-400" />
                )}
              </button>
            )}
          </div>
        </div>
      );
    }

    const heightClass = isComfortable ? 'h-48 p-6' : 'h-40 p-4';
    const iconSizeClass = isComfortable ? 'w-14 h-14 rounded-2xl text-2xl' : 'w-11 h-11 rounded-xl text-xl';
    const titleClass = isComfortable ? 'text-base mb-1' : 'text-sm mb-0.5';
    const descClass = isComfortable ? 'text-xs' : 'text-[11px]';

    return (
      <div
        key={app.id}
        className={`bg-white border border-gray-100 rounded-2xl cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col justify-between ${heightClass} relative group shadow-sm ${editMode ? 'cursor-move' : 'cursor-pointer'}`}
        data-lcars-color={(colorIndex % LCARS_COLORS) + 1}
        onClick={editMode ? undefined : () => handleApplicationClick(app)}
      >
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              {app.logo_url ? (
                <div className={`${iconSizeClass} bg-gray-50 flex items-center justify-center p-1.5 border border-gray-100 group-hover:border-indigo-100 transition-colors shadow-inner`}>
                  <img
                    src={app.logo_url}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      if (e.target.nextSibling?.className?.includes('fallback-icon')) return;
                      const fallback = document.createElement('div');
                      fallback.className = `${iconSizeClass} bg-indigo-50/70 border border-indigo-100 flex items-center justify-center shadow-inner fallback-icon`;
                      fallback.textContent = app.app_type === 'saml' ? '🔐' : app.app_type === 'bookmark' ? '🔖' : '🌐';
                      e.target.parentElement.insertBefore(fallback, e.target.nextSibling);
                    }}
                  />
                </div>
              ) : (
                <div className={`${iconSizeClass} bg-indigo-50/70 border border-indigo-100 flex items-center justify-center shadow-inner group-hover:bg-indigo-100/50 transition-colors`}>
                  {app.app_type === 'saml' ? '🔐' : app.app_type === 'bookmark' ? '🔖' : '🌐'}
                </div>
              )}
            </div>

            {showFavorite && (
              <button
                onClick={(e) => handleToggleFavorite(e, app)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-300 hover:text-yellow-400 group-hover:opacity-100 transition-all focus:outline-none"
                title={favoriteIds.has(app.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                {favoriteIds.has(app.id) ? (
                  <StarIcon className="h-5 w-5 text-yellow-400 scale-110" />
                ) : (
                  <StarOutlineIcon className="h-5 w-5 transition-transform hover:scale-110 text-gray-300 hover:text-yellow-400" />
                )}
              </button>
            )}
          </div>

          <h3 className={`font-bold text-gray-800 ${titleClass} tracking-tight truncate group-hover:text-indigo-600 transition-colors`}>{app.label}</h3>
          {app.description && (
            <p className={`${descClass} text-gray-500 line-clamp-2 leading-relaxed`}>{app.description}</p>
          )}
        </div>

        <div className="mt-2 flex items-center justify-end">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 group-hover:text-indigo-700 transition-colors">
            Open
            <svg className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        </div>
      </div>
    );
  };

  if (redirecting && redirectApp) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-light mb-6">
            <span className="text-3xl">🚀</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Redirecting to {redirectApp.label || redirectApp.name}
          </h2>
          <p className="text-gray-500 mb-8">
            You have one application assigned. Taking you there now...
          </p>
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <p className="text-sm text-gray-400">
            Redirecting in {redirectCountdown}...
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasNoCompany = userProfile && !userProfile.company_id;

  return (
    <PageLayout>
      {showMFAPopup && (
        <MFASetupPopup
          onClose={() => setShowMFAPopup(false)}
        />
      )}

      {hasNoCompany && (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 mb-8 text-amber-900 relative overflow-hidden shadow-lg shadow-amber-100/50 animate-fade">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-amber-500/5 rounded-full opacity-30 blur-2xl pointer-events-none" />
          
          <div className="flex items-start gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 flex items-center justify-center text-2xl flex-shrink-0 shadow-inner">
              ⚠️
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight text-amber-800">No Company Assigned</h3>
              <p className="text-sm mt-2 leading-relaxed">
                Your account is currently not assigned to any company in the system. Because of this, you cannot view or launch any portals or applications.
              </p>
              {userProfile.permission_level === 'global_admin' ? (
                <div className="mt-4 bg-white/50 border border-amber-200/50 rounded-2xl p-4 text-xs font-medium space-y-2">
                  <p className="text-amber-800 font-semibold">🔧 Next Steps for Administrators:</p>
                  <p>1. Go to the <Link to="/companies" className="underline font-bold text-indigo-600 hover:text-indigo-800">Company Management</Link> page under the <strong>Management</strong> menu.</p>
                  <p>2. Create a new company if you haven't yet, or select an existing company.</p>
                  <p>3. Go to the <strong>Users</strong> tab of that company and add yourself/other users to it.</p>
                </div>
              ) : (
                <p className="text-xs mt-3 font-medium text-amber-700">
                  Please contact your IT administrator or manager to request that your account be assigned to your company.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {sessionNotice && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-300 text-blue-800 rounded-lg flex justify-between items-center">
          <span className="text-sm font-medium">{sessionNotice}</span>
          <button onClick={() => setSessionNotice(null)} className="text-blue-600 hover:text-blue-900 font-bold text-lg leading-none">&times;</button>
        </div>
      )}

      {showWelcomeBanner && (
        <div 
          className="rounded-3xl p-6 mb-8 text-white relative overflow-hidden shadow-lg"
          style={{
            background: 'linear-gradient(to right, var(--color-primary, #4F46E5) 0%, var(--color-secondary, #7C3AED) 100%)'
          }}
        >
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-white/10 rounded-full opacity-30 blur-2xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 bg-white/10 rounded-full opacity-20 blur-xl pointer-events-none" />
          
          <div className="flex items-start gap-4 relative z-10">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center text-2xl flex-shrink-0 shadow-inner">
              ✨
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold tracking-tight">Welcome to TIMG Central</h3>
              <p className="text-sm text-white/70 mt-1.5 max-w-3xl leading-relaxed">
                Your central hub for accessing all your applications.
              </p>
            </div>
            <button
              onClick={() => { setShowWelcomeBanner(false); localStorage.setItem('dismissedWelcomeBanner', 'true'); }}
              className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Empty State - Proper Onboarding Experience (show early if no content) */}
      {(() => {
        // Check if categories have any actual apps in them
        const hasAppsInCategories = categories.some(cat => cat.applications && cat.applications.length > 0);
        const isEmpty = applications.length === 0 && !hasAppsInCategories && uncategorizedApps.length === 0 && portals.length === 0;
        console.log('🔍 Empty State Check:', {
          applications: applications.length,
          categories: categories.length,
          hasAppsInCategories: hasAppsInCategories,
          uncategorizedApps: uncategorizedApps.length,
          portals: portals.length,
          isEmpty: isEmpty
        });
        return isEmpty;
      })() ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-12 max-w-2xl mx-auto shadow-sm mt-10">
          <div className="text-center">
            <div className="text-6xl mb-6">👋</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No Applications Available</h2>
            <p className="text-gray-600 text-base mb-8 leading-relaxed max-w-md mx-auto">
              You don't have any applications assigned yet. If you believe you should see your apps here, contact your administrator for access.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-lg">📋</span> What happens next?
            </h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex gap-3">
                <span className="text-blue-600 font-semibold flex-shrink-0">1.</span>
                <span>An administrator will review your account and assign you to the appropriate company and roles</span>
              </div>
              <div className="flex gap-3">
                <span className="text-blue-600 font-semibold flex-shrink-0">2.</span>
                <span>You'll receive an email notification when applications are assigned to you</span>
              </div>
              <div className="flex gap-3">
                <span className="text-blue-600 font-semibold flex-shrink-0">3.</span>
                <span>Once configured, your applications will appear on this dashboard for easy access</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <div className="flex gap-3">
              <span className="text-xl flex-shrink-0">💡</span>
              <div>
                <h4 className="text-sm font-semibold text-amber-900 mb-1">Need immediate access?</h4>
                <p className="text-sm text-amber-800">
                  Contact your organization's administrator or IT support team to request application access and proper company assignment.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/settings" 
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <UserCircleIcon className="h-4 w-4" />
              Manage Your Profile
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Dashboard Toolbar: Layout Density Selector */}
          <div className="flex justify-between items-center mb-8 bg-white border border-gray-100 p-3 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
            <div className="text-xs font-bold text-gray-400 tracking-wider uppercase pl-2">My Applications</div>
            <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-xl border border-gray-100/50">
              <button
                onClick={() => { setLayoutDensity('comfortable'); localStorage.setItem('appLayoutDensity', 'comfortable'); }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${layoutDensity === 'comfortable' ? 'bg-white text-primary shadow-sm border border-gray-100/20' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <span>Comfortable</span>
              </button>
              <button
                onClick={() => { setLayoutDensity('standard'); localStorage.setItem('appLayoutDensity', 'standard'); }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${layoutDensity === 'standard' ? 'bg-white text-primary shadow-sm border border-gray-100/20' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <span>Standard</span>
              </button>
              <button
                onClick={() => { setLayoutDensity('compact'); localStorage.setItem('appLayoutDensity', 'compact'); }}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${layoutDensity === 'compact' ? 'bg-white text-primary shadow-sm border border-gray-100/20' : 'text-gray-500 hover:text-gray-900'}`}
              >
                <span>Compact</span>
              </button>
            </div>
          </div>
        </>
      )}

        {/* New Category Form (admin edit mode) */}
        {editMode && showNewCategoryForm && (
          <div className="mb-8 p-6 bg-white rounded-3xl border border-gray-100 shadow-xl max-w-2xl">
            <h3 className="text-base font-bold text-gray-800 tracking-tight mb-4">Create New Category</h3>
            <form onSubmit={handleCreateCategory} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Category Name *</label>
                  <input type="text" value={newCategory.name} onChange={e => setNewCategory({...newCategory, name: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-indigo-500 focus:ring-indigo-500" required placeholder="e.g., Support" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
                  <input type="text" value={newCategory.description} onChange={e => setNewCategory({...newCategory, description: e.target.value})} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-indigo-500 focus:ring-indigo-500" placeholder="Optional category description" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Select Category Icon Preset</label>
                <div className="flex flex-wrap gap-2 mb-3 bg-gray-50 p-3 rounded-2xl border border-gray-100/50">
                  {ICON_PRESETS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setNewCategory({...newCategory, icon: emoji})}
                      className={`w-9 h-9 rounded-xl flex items-center justify-center text-xl transition-all border ${
                        newCategory.icon === emoji
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-600 scale-110 shadow-sm shadow-indigo-100'
                          : 'border-transparent bg-white hover:bg-indigo-50/50 hover:border-indigo-200 text-gray-700'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">Or custom emoji:</span>
                  <input
                    type="text"
                    value={newCategory.icon}
                    onChange={e => setNewCategory({...newCategory, icon: e.target.value})}
                    className="w-14 px-2 py-1 border border-gray-200 rounded-xl text-center text-lg focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-50">
                <button type="button" onClick={() => setShowNewCategoryForm(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-md shadow-indigo-100 transition-all">Create Category</button>
              </div>
            </form>
          </div>
        )}

        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="mb-10 animate-fade">
            <div className="flex items-center mb-5">
              <span className="text-2xl mr-3 bg-white shadow-sm border border-gray-100 rounded-xl w-10 h-10 flex items-center justify-center">⭐️</span>
              <h2 className="text-lg font-bold text-gray-800 tracking-tight">My Favorites</h2>
            </div>
            <div className={`grid ${getGridColsClass()}`}>
              {favorites.map((app, idx) => (
                <AppCard key={`fav-${app.id}`} app={app} showFavorite={true} colorIndex={idx} layoutDensity={layoutDensity} />
              ))}
            </div>
          </div>
        )}

        {/* Categorized Applications */}
        {categories.map((category) => (
          (category.applications && category.applications.length > 0) || editMode ? (
            <div key={category.id} className={`mb-10 transition-all duration-300 ${draggedCategoryId === category.id ? 'opacity-40 scale-[0.98]' : ''}`}
              onDragOver={editMode ? handleDragOver : undefined}
              onDrop={editMode ? (e) => {
                if (draggedCategoryId !== null) {
                  handleCategoryDrop(e, category.id);
                } else {
                  handleDrop(e, category.id);
                }
              } : undefined}
            >
              <button
                onClick={() => !editMode && toggleCategoryCollapse(category.id)}
                draggable={editMode ? true : undefined}
                onDragStart={editMode ? (e) => handleCategoryDragStart(e, category.id) : undefined}
                onDragEnd={editMode ? () => setDraggedCategoryId(null) : undefined}
                className={`flex items-center mb-5 w-full text-left ${!editMode ? 'hover:bg-gray-100/75 rounded-2xl p-2.5 -ml-2.5 transition-all' : 'p-2.5 bg-gray-50/50 border border-gray-100 rounded-2xl cursor-grab active:cursor-grabbing'}`}
              >
                <span className="text-2xl mr-3 bg-white shadow-sm border border-gray-100 rounded-xl w-10 h-10 flex items-center justify-center">{category.icon}</span>
                {editingCategory?.id === category.id ? (
                  <div className="flex-1 bg-gray-50 border border-gray-100 rounded-3xl p-5 mb-4" onClick={e => e.stopPropagation()}>
                    <form onSubmit={handleUpdateCategory} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Category Name</label>
                          <input type="text" value={editingCategory.name} onChange={e => setEditingCategory({...editingCategory, name: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-indigo-500 focus:ring-indigo-500" required />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Description</label>
                          <input type="text" value={editingCategory.description || ''} onChange={e => setEditingCategory({...editingCategory, description: e.target.value})} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-indigo-500 focus:ring-indigo-500" placeholder="Optional" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Category Icon Preset</label>
                        <div className="flex flex-wrap gap-1.5 bg-white p-2.5 rounded-2xl border border-gray-100">
                          {ICON_PRESETS.map(emoji => (
                            <button
                              key={emoji}
                              type="button"
                              onClick={() => setEditingCategory({...editingCategory, icon: emoji})}
                              className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg transition-all border ${
                                editingCategory.icon === emoji
                                  ? 'border-indigo-500 bg-indigo-50 text-indigo-600 scale-110 shadow-sm'
                                  : 'border-transparent bg-transparent hover:bg-indigo-50/30 hover:border-indigo-100 text-gray-700'
                              }`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[11px] text-gray-400 font-medium">Or custom:</span>
                          <input
                            type="text"
                            value={editingCategory.icon}
                            onChange={e => setEditingCategory({...editingCategory, icon: e.target.value})}
                            className="w-12 px-1 py-0.5 border border-gray-200 rounded-lg text-center text-base focus:border-indigo-500"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200/50">
                        <button type="button" onClick={() => setEditingCategory(null)} className="px-3.5 py-1.5 border border-gray-200 rounded-xl text-xs font-semibold text-gray-600 hover:bg-white transition-all">Cancel</button>
                        <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-xl text-xs font-semibold transition-all">Save Changes</button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <>
                    <h2 className="text-lg font-bold text-gray-800 flex-1 tracking-tight">{category.name}</h2>
                    <span className="text-gray-400 text-xs font-semibold bg-gray-100 px-2 py-0.5 rounded-full mr-3">{category.applications?.length || 0}</span>
                    {editMode && (
                      <span className="flex gap-2 mr-3" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setEditingCategory(category)} className="text-indigo-600 hover:text-indigo-700 text-xs font-semibold bg-indigo-50 px-2.5 py-1 rounded-lg transition-colors">Edit</button>
                        <button onClick={() => handleDeleteCategory(category.id)} className="text-red-600 hover:text-red-700 text-xs font-semibold bg-red-50 px-2.5 py-1 rounded-lg transition-colors">Delete</button>
                      </span>
                    )}
                    {!editMode && (
                      <svg
                        className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${collapsedCategories.has(category.id) ? '' : 'rotate-180'}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </>
                )}
              </button>
              {!collapsedCategories.has(category.id) && (
                <div className={`grid ${getGridColsClass()} ${editMode ? 'border-2 border-dashed border-indigo-200 bg-indigo-50/5 rounded-2xl p-5' : ''}`}>
                  {category.applications?.map((app, idx) => (
                    <div key={app.id} 
                      draggable={editMode ? true : undefined}
                      onDragStart={editMode ? (e) => handleDragStart(e, app) : undefined}
                      onDragEnd={() => { setDraggedApp(null); setDraggedOverAppId(null); }}
                      onDragOver={editMode ? (e) => {
                        e.preventDefault();
                        if (draggedApp && draggedApp.id !== app.id) {
                          setDraggedOverAppId(app.id);
                        }
                      } : undefined}
                      onDragLeave={editMode ? () => setDraggedOverAppId(null) : undefined}
                      onDrop={editMode ? (e) => {
                        setDraggedOverAppId(null);
                        handleDropOnApp(e, category.id, app);
                      } : undefined}
                      className={`transition-all duration-200 ${
                        draggedOverAppId === app.id 
                          ? 'scale-[0.96] border-2 border-indigo-400 ring-4 ring-indigo-400/20 rounded-2xl' 
                          : ''
                      }`}
                      style={{ opacity: draggedApp?.id === app.id ? 0.5 : 1 }}
                    >
                      <AppCard app={app} showFavorite={!editMode} colorIndex={idx} layoutDensity={layoutDensity} />
                    </div>
                  ))}
                  {editMode && (!category.applications || category.applications.length === 0) && (
                    <p className="text-gray-400 text-center py-10 col-span-full font-medium text-sm">Drag applications here to assign</p>
                  )}
                </div>
              )}
            </div>
          ) : null
        ))}

        {/* Uncategorized Applications */}
        {uncategorizedApps.length > 0 && (
          <div className="mb-10"
            onDragOver={editMode ? handleDragOver : undefined}
            onDrop={editMode ? (e) => handleDrop(e, 0) : undefined}
          >
            <button
              onClick={() => toggleCategoryCollapse('uncategorized')}
              className="flex items-center mb-5 w-full text-left hover:bg-gray-100/75 rounded-2xl p-2.5 -ml-2.5 transition-all"
            >
              <span className="text-2xl mr-3 bg-white shadow-sm border border-gray-100 rounded-xl w-10 h-10 flex items-center justify-center">📋</span>
              <h2 className="text-lg font-bold text-gray-800 flex-1 tracking-tight">Other Applications</h2>
              <span className="text-gray-400 text-xs font-semibold bg-gray-100 px-2 py-0.5 rounded-full mr-3">{uncategorizedApps.length}</span>
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${collapsedCategories.has('uncategorized') ? '' : 'rotate-180'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!collapsedCategories.has('uncategorized') && (
              <div className={`grid ${getGridColsClass()} ${editMode ? 'border-2 border-dashed border-indigo-200 bg-indigo-50/5 rounded-2xl p-5' : ''}`}>
                {uncategorizedApps.map((app, idx) => (
                  <div key={app.id}
                    draggable={editMode ? true : undefined}
                    onDragStart={editMode ? (e) => handleDragStart(e, app) : undefined}
                    onDragEnd={() => { setDraggedApp(null); setDraggedOverAppId(null); }}
                    onDragOver={editMode ? (e) => {
                      e.preventDefault();
                      if (draggedApp && draggedApp.id !== app.id) {
                        setDraggedOverAppId(app.id);
                      }
                    } : undefined}
                    onDragLeave={editMode ? () => setDraggedOverAppId(null) : undefined}
                    onDrop={editMode ? (e) => {
                      setDraggedOverAppId(null);
                      handleDropOnApp(e, 0, app);
                    } : undefined}
                    className={`transition-all duration-200 ${
                      draggedOverAppId === app.id 
                        ? 'scale-[0.96] border-2 border-indigo-400 ring-4 ring-indigo-400/20 rounded-2xl' 
                        : ''
                    }`}
                    style={{ opacity: draggedApp?.id === app.id ? 0.5 : 1 }}
                  >
                    <AppCard app={app} showFavorite={!editMode} colorIndex={idx} layoutDensity={layoutDensity} />
                  </div>
                ))}
                {editMode && uncategorizedApps.length === 0 && (
                  <p className="text-gray-400 text-center py-10 col-span-full font-medium text-sm">Drag applications here to unassign</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Legacy Applications Section (when no categories exist) */}
        {categories.length === 0 && uncategorizedApps.length === 0 && applications.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center mb-5">
              <span className="text-2xl mr-3 bg-white shadow-sm border border-gray-100 rounded-xl w-10 h-10 flex items-center justify-center">📱</span>
              <h2 className="text-lg font-bold text-gray-800 tracking-tight">Applications</h2>
            </div>
            <div className={`grid ${getGridColsClass()}`}>
              {applications.map((app, idx) => (
                <AppCard key={app.id} app={app} showFavorite={true} colorIndex={idx} layoutDensity={layoutDensity} />
              ))}
            </div>
          </div>
        )}

        {/* Portals Section */}
        {portals.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center mb-5">
              <span className="text-2xl mr-3 bg-white shadow-sm border border-gray-100 rounded-xl w-10 h-10 flex items-center justify-center">🌐</span>
              <h2 className="text-lg font-bold text-gray-800 tracking-tight">My Portals</h2>
            </div>
            <div className={`grid ${getGridColsClass()}`}>
              {portals.map((portal) => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  onDelete={(portalId) => setPortals(portals.filter(p => p.id !== portalId))}
                  layoutDensity={layoutDensity}
                />
              ))}
              <CreatePortalCard onPortalCreated={(newPortal) => setPortals([...portals, newPortal])} />
            </div>
          </div>
        )}

      {/* Edit Categories - bottom centered */}
      <div className="flex justify-center mt-12 pb-6">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl shadow-sm border border-gray-100">
            {editMode && (
              <button
                onClick={() => setShowNewCategoryForm(true)}
                className="text-gray-700 hover:text-indigo-600 text-sm font-semibold border-r border-gray-100 pr-3 transition-colors"
              >
                + New Category
              </button>
            )}
            <button
              onClick={() => { setEditMode(!editMode); setShowNewCategoryForm(false); setEditingCategory(null); }}
              className={editMode ? 'text-sm font-semibold text-indigo-600' : 'text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors'}
            >
              {editMode ? '✓ Done Editing' : '✎ Edit Categories'}
            </button>
          </div>
        </div>

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Delete"
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

export default HomePage;